/**
 * WmsAlertsBell — header bell for agents / branch managers: new Social Pro orders that still need a script.
 * Fires a one-per-session login nudge via sonner.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, ScrollText, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const NUDGE_KEY = 'nc-wms-alert-nudged';

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(String(s).replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const openScript = (orderId) => {
  if (orderId) { try { sessionStorage.setItem('nc-preselect-wms-order', String(orderId)); } catch { /* private mode */ } }
  window.dispatchEvent(new CustomEvent('nichesocial:open-module', { detail: 'script' }));
};

export function WmsAlertsBell() {
  const [data, setData] = useState({ unseen: 0, alerts: [] });
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const load = () => axios.get(`${API}/api/wms/alerts`).then((r) => { setData(r.data); return r.data; }).catch(() => null);

  useEffect(() => {
    load().then((d) => {
      if (!d || !d.unseen) return;
      let nudged = false;
      try { nudged = sessionStorage.getItem(NUDGE_KEY) === '1'; } catch { /* private mode */ }
      if (nudged) return;
      try { sessionStorage.setItem(NUDGE_KEY, '1'); } catch { /* private mode */ }
      const first = d.alerts.find((a) => !a.seen) || d.alerts[0];
      toast(`${d.unseen} new Social Pro ${d.unseen === 1 ? 'order needs' : 'orders need'} a script`, {
        description: first ? `${first.address}${d.unseen > 1 ? ` and ${d.unseen - 1} more` : ''}` : undefined,
        duration: 9000,
        action: { label: 'Write the script', onClick: () => openScript(d.unseen === 1 && first ? first.order_id : null) },
      });
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    const refresh = () => { load(); };
    window.addEventListener('nichesocial:alerts-refresh', refresh);
    window.addEventListener('focus', refresh);
    const t = setInterval(refresh, 5 * 60 * 1000);
    return () => { window.removeEventListener('nichesocial:alerts-refresh', refresh); window.removeEventListener('focus', refresh); clearInterval(t); };
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) load();
    if (next && data.unseen > 0) {
      axios.post(`${API}/api/wms/alerts/seen`).then(() => setData((d) => ({ ...d, unseen: 0, alerts: d.alerts.map((a) => ({ ...a, seen: true })) }))).catch(() => {});
    }
  };
  const dismiss = (e, a) => {
    e.stopPropagation();
    setData((d) => ({ ...d, alerts: d.alerts.filter((x) => x.id !== a.id) }));
    axios.post(`${API}/api/wms/alerts/${a.order_id}/dismiss`).catch(() => {});
  };

  return (
    <div className="wms-bell-wrap" ref={wrapRef}>
      <button type="button" className={`header-cog wms-bell ${data.unseen > 0 ? 'has-new' : ''}`} onClick={toggle} aria-label={`Social Pro order alerts${data.unseen ? ` (${data.unseen} new)` : ''}`} title="Social Pro order alerts" data-testid="wms-alerts-bell">
        <Bell size={18} />
        {data.unseen > 0 && <span className="wms-bell-badge" data-testid="wms-alerts-badge">{data.unseen > 9 ? '9+' : data.unseen}</span>}
      </button>
      {open && (
        <div className="wms-bell-panel" data-testid="wms-alerts-panel">
          <div className="wms-bell-head">
            <strong>Orders needing a script</strong>
            <span>{data.alerts.length} open</span>
          </div>
          {data.alerts.length === 0 ? (
            <p className="wms-bell-empty" data-testid="wms-alerts-empty">You're all caught up — every Social Pro order has its script.</p>
          ) : (
            <ul className="wms-bell-list">
              {data.alerts.map((a) => (
                <li key={a.id} className={a.seen ? '' : 'is-new'} data-testid={`wms-alert-${a.order_id}`}>
                  <button type="button" className="wms-bell-item" onClick={() => { setOpen(false); openScript(a.order_id); }} data-testid={`wms-alert-open-${a.order_id}`}>
                    <span className="wms-bell-addr">{a.address || `Order #${a.order_id}`}</span>
                    <span className="wms-bell-meta">#{a.order_id}{a.branch_name ? ` · ${a.branch_name}` : ''}{a.wms_created_at ? ` · booked ${fmtDate(a.wms_created_at)}` : ''}</span>
                    <span className="wms-bell-cta"><ScrollText size={12} /> Write the script</span>
                  </button>
                  <button type="button" className="wms-bell-dismiss" onClick={(e) => dismiss(e, a)} aria-label="Dismiss" data-testid={`wms-alert-dismiss-${a.order_id}`}><X size={13} /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
